import { dasherizeParts, FlagState, Stat } from "@timing71/common";
import { RaceControlMessage } from '@timing71/common/messages';
import { parseTime, titleCase } from "../utils.js";

const SECTOR_STATS = [
  Stat.S1,
  Stat.BS1,
  Stat.S2,
  Stat.BS2,
  Stat.S3,
  Stat.BS3,
  Stat.S4,
  Stat.BS4,
  Stat.S5,
  Stat.BS5,
];

const TRACK_DATA = [
  'Weather',
  'Track',
  'Air temp',
  'Track temp',
  'Air pressure',
  'Humidity',
  'Wind dir',
  'Wind speed'
];

const FLAG_MAP = {
  'Green': FlagState.GREEN,
  'Red': FlagState.RED,
  'Yellow': FlagState.SC,
  'SafetyCar': FlagState.SC,
  'FCY': FlagState.FCY,
  'Finish': FlagState.CHEQUERED,
  'Complete': FlagState.CHEQUERED,
  'Pending': FlagState.NONE,
  'Active': FlagState.NONE
};

const CAR_STATE_MAP = {
  0: 'RUN',
  1: 'FIN',
  2: 'DSQ',
  3: 'STOP',
  4: 'PIT'
}

const getTimingSectors = (state) => (state.track?.sectors || []).filter(s => !s.isSpeedTrap);

const getSectorCount = (state) => getTimingSectors(state).length;

export const getManifest = (state) => {
  const colSpec = [
    Stat.NUM,
    Stat.STATE,
    Stat.CLASS,
    Stat.POS_IN_CLASS,
    Stat.DRIVER,
    Stat.TEAM,
    Stat.CAR,
    Stat.LAPS,
    Stat.GAP,
    Stat.INT
  ].concat(
    SECTOR_STATS.slice(0, getSectorCount(state) * 2)
  ).concat([
    Stat.LAST_LAP,
    Stat.BEST_LAP,
    Stat.PITS
  ]);

  return {
    name: state.series || '',
    description: dasherizeParts(
      state.track?.displayName,
      titleCase(state.name)
    ),
    colSpec,
    trackDataSpec: TRACK_DATA
  };
}

const mapCar = (c, currentSectors, bestSectors, lastSeenSectors, sectors, fastestLap) => {

  const sectorCols = sectors.flatMap(
    s => {
      const last = currentSectors?.[s.key];
      const best = bestSectors?.[s.key];

      const isOverallBest = s.sessionBest?.id === c.id;

      const isPB = last && last === best;

      const isOld = lastSeenSectors[c.id] && s.key.localeCompare(lastSeenSectors[c.id]) > 0;

      return [
        [currentSectors?.[s.key], isPB ? isOverallBest ? 'sb': 'pb' : isOld ? 'old' : ''],
        [bestSectors?.[s.key], isOverallBest ? 'sb' : 'old']
      ]
    }
  );

  const lastLap = parseTime(c.lastLapTime);
  const bestLap = parseTime(c.result?.fastLapTime);

  const isFastestLap = fastestLap?.id === c.id;

  return [
    c.no,
    CAR_STATE_MAP[c.state] || `${c.state}`,
    c.subClass || c.primaryClass || '',
    c.result?.pic || '',
    c.currentDriver ? `${c.currentDriver.lastName}, ${c.currentDriver.firstName}` : c.name,
    c.team,
    c.vehicle,
    c.result?.laps,
    c.result?.gap,
    c.result?.diff,
    ...sectorCols,
    [lastLap, lastLap === bestLap ? isFastestLap ? 'sb-new' : 'pb' : ''],
    [bestLap, isFastestLap ? 'sb' : 'old'],
    c.result?.pitStops
  ]
}

const mapCars = ({ classification, currentSectors, bestSectors, fastestLap, lastSeenSectors }, sectors) => {

  const orderedCars = [...classification].sort(
    (a, b) => (a.result?.position || 99999) - (b.result?.position || 99999)
  );

  return orderedCars.map(
    car => mapCar(car, currentSectors[car.id], bestSectors[car.id], lastSeenSectors, sectors, fastestLap)
  );
}

export const getState = (state) => {
  const timeDelta = (Date.now() - (state.stateUpdated || 0)) / 1000;
  const pauseClocks = !state.sessionClock?.running;

  const timeRemain = Math.max(
    parseTime(state.sessionClock.timeToGo) - (pauseClocks ? 0 : timeDelta),
    0
  );

  const timeElapsed = state.actualStart ? (Date.now() - state.actualStart) / 1000 : undefined;

  return {
    cars: mapCars(state, getTimingSectors(state)),
    session: {
      flagState: FLAG_MAP[state.sessionFlag] || FlagState.NONE,
      pauseClocks,
      timeRemain,
      timeElapsed,
      trackData: [
        state.weatherConditions,
        state.trackConditions,
        [state.weather.airTemp, '°C'],
        [state.weather.trackTemp, '°C'],
        [state.weather.airPressure, 'mb'],
        [state.weather.humidity, '%'],
        [state.weather.windDirection, '°'],
        [((state.weather.windSpeed || 0) * 2.23694).toFixed(1), 'mph']
      ]
    }
  }
}

export const getRaceControlMessage = (msg) => {
  return new RaceControlMessage(msg.text).toCTDFormat();
}
